import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export function getReportFileName(ext) {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `dashboard-performance-report-${yyyy}-${mm}-${dd}.${ext}`;
}

export function formatCurrency(value) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      Number(value || 0)
    );
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
}

function csvEscape(value) {
  const stringValue = String(value ?? "");
  if (/[",\n]/.test(stringValue)) return `"${stringValue.replace(/"/g, "\"\"")}"`;
  return stringValue;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function humanizeActivity(activity) {
  const type = String(activity?.type || "");
  const actorName = activity?.actor?.name || activity?.actor?.email || "A user";
  if (type.startsWith("auth.login")) return `${actorName} logged into the dashboard`;
  if (type.startsWith("brand.created")) return "A new brand was created";
  if (type.startsWith("brand.updated")) return "Brand information was updated";
  if (type.startsWith("brand.deleted")) return "A brand record was deleted";
  if (type.startsWith("user.created")) return "A new user account was created";
  if (type.startsWith("user.updated")) return "User details were updated";
  if (type.startsWith("user.deleted")) return "A user account was deleted";
  if (type.startsWith("layout.saved")) return "A layout was created or saved";
  if (type.startsWith("layout.updated")) return "A layout was updated";
  if (type.startsWith("layout.duplicated")) return "A layout was duplicated";
  if (type.startsWith("layout.deleted")) return "A layout was deleted";
  const raw = String(activity?.message || "").replace(/\b[a-f0-9]{24}\b/gi, "").trim();
  return raw || "System activity recorded";
}

export function groupActivities(recentActivities = []) {
  const grouped = {
    "Authentication Activities": [],
    "Brand Activities": [],
    "User Management Activities": [],
    "Layout Activities": [],
  };
  recentActivities.forEach((a) => {
    const type = String(a?.type || "");
    if (type.startsWith("auth.")) grouped["Authentication Activities"].push(a);
    else if (type.startsWith("brand.")) grouped["Brand Activities"].push(a);
    else if (type.startsWith("user.")) grouped["User Management Activities"].push(a);
    else if (type.startsWith("layout.")) grouped["Layout Activities"].push(a);
  });
  return grouped;
}

function applyAutoWidth(ws, rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  ws["!cols"] = headers.map((h) => {
    const maxLen = Math.max(h.length, ...rows.map((r) => String(r[h] ?? "").length));
    return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
  });
}

function styleHeaderRow(ws, keys) {
  keys.forEach((_, idx) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: idx });
    if (ws[cellRef]) {
      ws[cellRef].s = { font: { bold: true } };
    }
  });
  ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: keys.length - 1, r: 0 } }) };
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
}

export async function generateCorporatePdf(report, viewer) {
  const generatedAt = new Date();
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const accent = [49, 70, 153];
  const textDark = [35, 35, 35];

  doc.setFillColor(245, 247, 252);
  doc.rect(0, 0, 595, 120, "F");
  doc.setTextColor(...accent);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("REACT ADMIN SYSTEM", 40, 32);
  doc.setTextColor(...textDark);
  doc.setFontSize(24);
  doc.text("Dashboard Performance Report", 40, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.text("Business Activity & System Overview", 40, 82);
  doc.setFontSize(10);
  doc.text(`Generated: ${generatedAt.toLocaleString()}`, 40, 101);
  doc.text(`Generated by: ${viewer.name || viewer.email || "System"} (${viewer.role || "unknown"})`, 40, 115);
  doc.setDrawColor(205, 213, 227);
  doc.line(40, 128, 555, 128);

  const kpis = [
    ["Total Brands", report.summary.totalBrands, "Active brands currently registered in the system"],
    ["Total Layouts", report.summary.totalLayouts, "Layouts available across all brands"],
    ["Total Users", report.summary.totalUsers, "Users with access to this platform"],
    ["Revenue Generated", formatCurrency(report.summary.revenueGenerated || 0), "Revenue tracked from system activities"],
  ];
  const cardY = 146;
  const cardW = 246;
  const cardH = 74;
  kpis.forEach((kpi, idx) => {
    const x = idx % 2 === 0 ? 40 : 309;
    const y = cardY + Math.floor(idx / 2) * (cardH + 14);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, cardW, cardH, 8, 8, "F");
    doc.setDrawColor(220, 225, 236);
    doc.roundedRect(x, y, cardW, cardH, 8, 8, "S");
    doc.setTextColor(...accent);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(String(kpi[0]), x + 14, y + 20);
    doc.setTextColor(...textDark);
    doc.setFontSize(18);
    doc.text(String(kpi[1]), x + 14, y + 43);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(String(kpi[2]), x + 14, y + 60);
  });

  // Simple visual summary bars
  const chartY = 322;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Visual Summary", 40, chartY);
  const series = [
    { label: "Brands", value: Number(report.summary.totalBrands || 0), color: [59, 130, 246] },
    { label: "Layouts", value: Number(report.summary.totalLayouts || 0), color: [16, 185, 129] },
    { label: "Users", value: Number(report.summary.totalUsers || 0), color: [245, 158, 11] },
  ];
  const max = Math.max(1, ...series.map((s) => s.value));
  series.forEach((s, i) => {
    const y = chartY + 18 + i * 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`${s.label} (${s.value})`, 40, y + 8);
    doc.setDrawColor(225, 225, 225);
    doc.line(120, y + 4, 520, y + 4);
    doc.setDrawColor(...s.color);
    doc.setLineWidth(8);
    doc.line(120, y + 4, 120 + (400 * s.value) / max, y + 4);
    doc.setLineWidth(1);
  });

  const grouped = groupActivities(report.recentActivities || []);
  let startY = 410;
  Object.entries(grouped).forEach(([sectionName, items]) => {
    if (!items.length) return;
    if (startY > 710) {
      doc.addPage();
      startY = 50;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(sectionName, 40, startY);
    autoTable(doc, {
      startY: startY + 8,
      head: [["Sr. No.", "Activity Type", "Description", "User Email", "Date & Time"]],
      body: items.map((a, idx) => [
        idx + 1,
        a.type || "",
        humanizeActivity(a),
        a.actor?.email || "",
        a.createdAt ? new Date(a.createdAt).toLocaleString() : "",
      ]),
      margin: { left: 40, right: 40 },
      styles: { fontSize: 9, textColor: 35, lineColor: [219, 223, 230] },
      headStyles: { fillColor: [235, 238, 245], textColor: 35, fontStyle: "bold" },
      theme: "grid",
    });
    startY = doc.lastAutoTable.finalY + 18;
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(230, 230, 230);
    doc.line(40, 812, 555, 812);
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text("React Admin System", 40, 826);
    doc.text("Confidential / Internal Use Only", 230, 826);
    doc.text(`Page ${i} of ${pages}`, 500, 826);
  }

  doc.save(getReportFileName("pdf"));
}

export async function generateExcelReport(report, viewer) {
  const wb = XLSX.utils.book_new();
  const createdAt = new Date().toLocaleString();
  const summaryRows = [
    { Metric: "Report Title", Value: "Dashboard Performance Report" },
    { Metric: "Generated At", Value: createdAt },
    { Metric: "Generated By", Value: `${viewer.name || viewer.email || "System"} (${viewer.role || "unknown"})` },
    { Metric: "Total Brands", Value: report.summary.totalBrands },
    { Metric: "Total Layouts", Value: report.summary.totalLayouts },
    { Metric: "Total Users", Value: report.summary.totalUsers },
    { Metric: "Revenue Generated", Value: report.summary.revenueGenerated || 0 },
  ];
  const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
  applyAutoWidth(summaryWs, summaryRows);
  styleHeaderRow(summaryWs, Object.keys(summaryRows[0]));
  XLSX.utils.book_append_sheet(wb, summaryWs, "Executive Summary");

  const activityRows = (report.recentActivities || []).map((a, idx) => ({
    "Sr. No.": idx + 1,
    "Activity Type": a.type || "",
    Description: humanizeActivity(a),
    "User Email": a.actor?.email || "",
    "Date & Time": a.createdAt ? new Date(a.createdAt).toLocaleString() : "",
  }));
  const activitiesWs = XLSX.utils.json_to_sheet(activityRows);
  applyAutoWidth(activitiesWs, activityRows);
  if (activityRows.length) styleHeaderRow(activitiesWs, Object.keys(activityRows[0]));
  XLSX.utils.book_append_sheet(wb, activitiesWs, "Recent Activities");

  const brandsRows = (report.brands || []).map((b) => ({
    "Registration ID": b.registrationId || "",
    "Client Name": b.clientName || "",
    "Brand Name": b.brandName || "",
    "Contact Number": b.contactNumber || "",
    Email: b.email || "",
    Website: b.website || "",
    City: b.city || "",
    "Zip Code": b.zipCode || "",
  }));
  const brandsWs = XLSX.utils.json_to_sheet(brandsRows);
  applyAutoWidth(brandsWs, brandsRows);
  if (brandsRows.length) styleHeaderRow(brandsWs, Object.keys(brandsRows[0]));
  XLSX.utils.book_append_sheet(wb, brandsWs, "Brands");

  const usersRows = (report.users || []).map((u) => ({
    Name: u.name || "",
    Email: u.email || "",
    Role: u.role || "",
    "Created At": u.createdAt ? new Date(u.createdAt).toLocaleString() : "",
  }));
  const usersWs = XLSX.utils.json_to_sheet(usersRows);
  applyAutoWidth(usersWs, usersRows);
  if (usersRows.length) styleHeaderRow(usersWs, Object.keys(usersRows[0]));
  XLSX.utils.book_append_sheet(wb, usersWs, "Users");

  const layoutRows = (report.layouts || []).map((l) => ({
    Title: l.title || "",
    "File Name": l.fileName || "",
    Brand: l.brandName || "",
    "Created By": l.createdByName || l.createdByEmail || "",
    "Created At": l.createdAt ? new Date(l.createdAt).toLocaleString() : "",
  }));
  const layoutWs = XLSX.utils.json_to_sheet(layoutRows);
  applyAutoWidth(layoutWs, layoutRows);
  if (layoutRows.length) styleHeaderRow(layoutWs, Object.keys(layoutRows[0]));
  XLSX.utils.book_append_sheet(wb, layoutWs, "Layouts");

  XLSX.writeFile(wb, getReportFileName("xlsx"));
}

export async function generateCsvReport(report, viewer) {
  const lines = [];
  lines.push("Dashboard Performance Report");
  lines.push(`Generated At,${csvEscape(new Date().toLocaleString())}`);
  lines.push(`Generated By,${csvEscape(`${viewer.name || viewer.email || "System"} (${viewer.role || "unknown"})`)}`);
  lines.push("");
  lines.push("Executive Summary");
  lines.push("Metric,Value");
  lines.push(`Total Brands,${report.summary.totalBrands}`);
  lines.push(`Total Layouts,${report.summary.totalLayouts}`);
  lines.push(`Total Users,${report.summary.totalUsers}`);
  lines.push(`Revenue Generated,${report.summary.revenueGenerated || 0}`);
  lines.push("");
  lines.push("Recent Activities");
  lines.push("Sr. No.,Activity Type,Description,User Email,Date & Time");
  (report.recentActivities || []).forEach((a, idx) => {
    lines.push(
      [
        idx + 1,
        csvEscape(a.type || ""),
        csvEscape(humanizeActivity(a)),
        csvEscape(a.actor?.email || ""),
        csvEscape(a.createdAt ? new Date(a.createdAt).toLocaleString() : ""),
      ].join(",")
    );
  });
  const blob = new Blob([`${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getReportFileName("csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function openPrintReport(report, viewer) {
  const grouped = groupActivities(report.recentActivities || []);
  const chartRows = [
    { label: "Brands", value: Number(report.summary.totalBrands || 0) },
    { label: "Layouts", value: Number(report.summary.totalLayouts || 0) },
    { label: "Users", value: Number(report.summary.totalUsers || 0) },
  ];
  const chartMax = Math.max(1, ...chartRows.map((x) => x.value));
  const chartHtml = chartRows
    .map(
      (x) =>
        `<div class="bar-row"><span>${escapeHtml(x.label)} (${x.value})</span><div class="bar"><div class="fill" style="width:${(
          (x.value / chartMax) *
          100
        ).toFixed(1)}%"></div></div></div>`
    )
    .join("");
  const sectionHtml = Object.entries(grouped)
    .filter(([, items]) => items.length > 0)
    .map(([name, items]) => {
      const rows = items
        .map(
          (a, idx) =>
            `<tr><td>${idx + 1}</td><td>${escapeHtml(a.type || "")}</td><td>${escapeHtml(
              humanizeActivity(a)
            )}</td><td>${escapeHtml(a.actor?.email || "")}</td><td>${escapeHtml(
              a.createdAt ? new Date(a.createdAt).toLocaleString() : ""
            )}</td></tr>`
        )
        .join("");
      return `<h3>${escapeHtml(name)}</h3><table><thead><tr><th>Sr. No.</th><th>Activity Type</th><th>Description</th><th>User Email</th><th>Date & Time</th></tr></thead><tbody>${rows}</tbody></table>`;
    })
    .join("");

  const win = window.open("", "_blank", "noopener,noreferrer,width=1024,height=900");
  if (!win) throw new Error("Unable to open print window");
  win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Dashboard Performance Report</title>
  <style>
    @page { size: A4 portrait; margin: 14mm; }
    body { font-family: Arial, sans-serif; color: #111; background: #fff; font-size: 12px; margin: 0; }
    .page { width: 100%; }
    h1 { margin: 0; font-size: 24px; }
    h2 { margin: 2px 0 0; font-size: 14px; color: #3f4a63; }
    h3 { margin: 18px 0 8px; font-size: 14px; color: #2d3850; }
    .top { border-bottom: 1px solid #d7dce6; padding-bottom: 10px; margin-bottom: 12px; }
    .meta { margin-top: 8px; color: #444; }
    .kpis { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; margin: 10px 0 14px; }
    .card { border: 1px solid #d7dce6; border-radius: 8px; padding: 8px; }
    .card .label { color: #2d3850; font-weight: 600; }
    .card .value { font-size: 20px; margin: 2px 0; font-weight: 700; }
    .card .desc { color: #555; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-bottom: 10px; }
    th, td { border: 1px solid #dee3eb; padding: 5px; text-align: left; vertical-align: top; word-break: break-word; }
    th { background: #f6f7fb; font-weight: 700; }
    .bar-row { display: grid; grid-template-columns: 120px 1fr; align-items: center; gap: 8px; margin: 6px 0; }
    .bar { height: 10px; border-radius: 5px; background: #eef1f6; overflow: hidden; }
    .fill { height: 100%; background: #4f63b8; }
    footer { margin-top: 12px; padding-top: 8px; border-top: 1px solid #ddd; color: #555; font-size: 10px; display: flex; justify-content: space-between; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="page">
    <div class="top">
      <div style="font-size:11px;color:#4f63b8;font-weight:700;">REACT ADMIN SYSTEM</div>
      <h1>Dashboard Performance Report</h1>
      <h2>Business Activity & System Overview</h2>
      <div class="meta">
        Generated: ${escapeHtml(new Date().toLocaleString())}<br/>
        Generated by: ${escapeHtml(`${viewer.name || viewer.email || "System"} (${viewer.role || "unknown"})`)}
      </div>
    </div>
    <div class="kpis">
      <div class="card"><div class="label">Total Brands</div><div class="value">${report.summary.totalBrands}</div><div class="desc">Active brands currently registered in the system</div></div>
      <div class="card"><div class="label">Total Layouts</div><div class="value">${report.summary.totalLayouts}</div><div class="desc">Layouts available across all brands</div></div>
      <div class="card"><div class="label">Total Users</div><div class="value">${report.summary.totalUsers}</div><div class="desc">Users with access to this platform</div></div>
      <div class="card"><div class="label">Revenue Generated</div><div class="value">${escapeHtml(
        formatCurrency(report.summary.revenueGenerated || 0)
      )}</div><div class="desc">Revenue tracked from system activities</div></div>
    </div>
    <h3>Visual Summary</h3>
    ${chartHtml}
    ${sectionHtml}
    <footer>
      <span>React Admin System</span>
      <span>Confidential / Internal Use Only</span>
      <span>Printed Report</span>
    </footer>
  </div>
</body>
</html>`);
  win.document.close();
  win.focus();
  win.print();
}
